import { yupResolver } from "@hookform/resolvers/yup";
import Form from "antd/lib/form";
import type { InputRef } from "antd/lib/input";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { ReactNode, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input } from "@sol/uikit/data-entry/Input/Input";
import { Button } from "@sol/uikit/general/Button/Button";
export type AddUserFormData = yup.InferType<typeof schema>;

type Props = {
    title: string;
    subtitle?: string | ReactNode;
    onUserAdd: (formData: AddUserFormData) => void;
    showEmailOnly?: boolean;
    isLoading?: boolean;
    apiErrorMessage?: string;
    className?: string;
};

const schema = yup
    .object()
    .shape({
        email: yup.string().required().email(),
        name: yup.string(),
        surname: yup.string(),
    })
    .required();

const defaultValues = { email: "", name: "", surname: "" };

export const UserPickerForm = ({
    title,
    subtitle,
    onUserAdd: onSubmit,
    isLoading = false,
    showEmailOnly,
    apiErrorMessage,
    className,
}: Props) => {
    const [t] = useTranslation();
    const firstErrorFieldRef = useRef<InputRef>(null);
    const emailFieldRef = useRef<InputRef>(null);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, errors },
    } = useForm<AddUserFormData>({
        resolver: yupResolver(schema),
        defaultValues,
    });

    useEffect(() => {
        firstErrorFieldRef.current?.focus();
    }, [errors]);

    const onFormSubmit = (formData: AddUserFormData) => {
        onSubmit(formData);
        reset(defaultValues);
        emailFieldRef?.current?.focus();
    };

    return (
        <div className={clsx(className, "flex h-full flex-col")}>
            <div className="flex flex-col gap-3">
                <Typography.Title level={2} className="text-ant-h2">
                    {title}
                </Typography.Title>
                {typeof subtitle === "string" ? <Typography.Text>{subtitle}</Typography.Text> : subtitle}
            </div>
            <form
                className="flex flex-1 flex-col justify-center gap-4"
                onSubmit={handleSubmit(onFormSubmit)}
                data-cy="add-user-form"
                noValidate
            >
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState: { invalid, error } }) => (
                        <Form.Item
                            label={t("component.shared.UserPickerForm.inputs.email.label")}
                            extra={t("component.shared.UserPickerForm.inputs.email.helper")}
                            help={error?.message || apiErrorMessage}
                            validateStatus={invalid || apiErrorMessage ? "error" : "success"}
                            layout="vertical"
                            required
                        >
                            <Input
                                placeholder="..."
                                data-cy="user-picker-email"
                                {...field}
                                ref={errors["email"] ? firstErrorFieldRef : emailFieldRef}
                            />
                        </Form.Item>
                    )}
                />

                {!showEmailOnly &&
                    ["name", "surname"].map((fieldName: "name" | "surname") => (
                        <Controller
                            key={fieldName}
                            control={control}
                            name={fieldName}
                            render={({ field, fieldState: { invalid, error } }) => (
                                <Form.Item
                                    label={t(`component.shared.UserPickerForm.inputs.${fieldName}.label`)}
                                    help={error?.message}
                                    validateStatus={invalid ? "error" : "success"}
                                    layout="vertical"
                                    required
                                >
                                    <Input
                                        placeholder="..."
                                        data-cy={`user-picker-${fieldName}`}
                                        {...field}
                                        ref={errors[fieldName] && firstErrorFieldRef}
                                    />
                                </Form.Item>
                            )}
                        />
                    ))}

                <Button
                    data-cy="check-email-button"
                    htmlType="submit"
                    type="primary"
                    disabled={!isDirty || isLoading}
                    className="ml-auto flex"
                    loading={isLoading}
                >
                    {t("component.shared.UserPickerForm.add")}
                </Button>
            </form>
        </div>
    );
};
